import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  Eraser,
  Filter,
  HelpCircle,
  Keyboard,
  MessageSquarePlus,
  Pause,
  Play,
  Plug,
  Settings,
} from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePanelStore } from '@/features/panel/store/panel-store';
import type { ChatFeedStore } from '@/features/chat-feed/store';
import { ISSUES_URL, REPO_URL } from '@/lib/config';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store: ChatFeedStore | null;
  onOpenShortcuts: () => void;
  onOpenFeedback?: () => void;
}

type RunFn = (fn: () => void) => () => void;

// Grupo Feed é extraído pra que as chamadas a `store(selector)` (zustand hooks)
// só existam quando store está garantidamente presente. Sem isso, alternar
// store=null↔store=presente entre renders quebra a contagem de hooks do React.
function FeedGroup({ store, run }: { store: ChatFeedStore; run: RunFn }) {
  const paused = store((s) => s.paused);
  const filter = store((s) => s.filter);

  return (
    <>
      <CommandSeparator />
      <CommandGroup heading="Feed">
        <CommandItem
          onSelect={run(() => {
            const s = store.getState();
            if (paused) s.resume();
            else s.pause();
          })}
          keywords={['pausa', 'pause', 'retomar', 'resume', 'parar']}
        >
          {paused ? (
            <Play size={14} strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <Pause size={14} strokeWidth={1.75} aria-hidden="true" />
          )}
          <span>{paused ? 'Retomar feed' : 'Pausar feed'}</span>
        </CommandItem>
        <CommandItem
          onSelect={run(() => store.getState().clearLocal())}
          keywords={['limpar', 'clear', 'buffer', 'reset']}
        >
          <Eraser size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>Limpar buffer</span>
        </CommandItem>
        {(filter.platform !== 'all' || filter.onlyMods) && (
          <CommandItem
            onSelect={run(() =>
              store.getState().setFilter({ platform: 'all', onlyMods: false }),
            )}
            keywords={['limpar', 'filtros', 'reset']}
          >
            <Filter size={14} strokeWidth={1.75} aria-hidden="true" />
            <span>Limpar filtros</span>
          </CommandItem>
        )}
        <CommandItem
          onSelect={run(() => store.getState().setFilter({ platform: 'twitch' }))}
          keywords={['filtrar', 'só', 'twitch']}
        >
          <Filter size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>Filtrar apenas Twitch</span>
        </CommandItem>
        <CommandItem
          onSelect={run(() => store.getState().setFilter({ platform: 'youtube' }))}
          keywords={['filtrar', 'só', 'youtube']}
        >
          <Filter size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>Filtrar apenas YouTube</span>
        </CommandItem>
      </CommandGroup>
    </>
  );
}

export function CommandPalette({
  open,
  onOpenChange,
  store,
  onOpenShortcuts,
}: Props) {
  const navigate = useNavigate();
  const setDiscoveryOpen = usePanelStore((s) => s.setDiscoverySheetOpen);

  // Closure: roda a ação e fecha o palette.
  const run = useMemo<RunFn>(
    () => (fn) => () => {
      fn();
      onOpenChange(false);
    },
    [onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[560px] max-h-[480px] p-0"
        // cmdk gerencia foco — não roubar via Radix autoFocus do close.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Busca de ações, navegação e atalhos.
        </DialogDescription>
        <Command label="Command palette">
          <CommandInput placeholder="Buscar ação, página ou atalho…" autoFocus />
          <CommandList>
            <CommandEmpty>Nenhum resultado.</CommandEmpty>

            <CommandGroup heading="Navegar">
              <CommandItem
                onSelect={run(() => navigate('/settings'))}
                keywords={['configurações', 'tema', 'aparência', 'preferências']}
              >
                <Settings size={14} strokeWidth={1.75} aria-hidden="true" />
                <span>Configurações</span>
              </CommandItem>
              <CommandItem
                onSelect={run(() => setDiscoveryOpen(true))}
                keywords={['conectar', 'live', 'streamer', 'twitch', 'youtube']}
              >
                <Plug size={14} strokeWidth={1.75} aria-hidden="true" />
                <span>Conectar live</span>
              </CommandItem>
            </CommandGroup>

            {store && <FeedGroup store={store} run={run} />}

            <CommandSeparator />
            <CommandGroup heading="Ajuda">
              <CommandItem
                onSelect={run(() => onOpenShortcuts())}
                keywords={['atalhos', 'teclado', 'shortcuts', 'hotkeys']}
              >
                <Keyboard size={14} strokeWidth={1.75} aria-hidden="true" />
                <span>Mostrar atalhos</span>
                <CommandShortcut>?</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={run(() => {
                  window.open(ISSUES_URL, '_blank', 'noopener,noreferrer');
                })}
                keywords={['feedback', 'bug', 'sugestão', 'issue']}
              >
                <MessageSquarePlus size={14} strokeWidth={1.75} aria-hidden="true" />
                <span>Enviar feedback</span>
              </CommandItem>
              <CommandItem
                onSelect={run(() => {
                  window.open(REPO_URL, '_blank', 'noopener,noreferrer');
                })}
                keywords={['ajuda', 'docs', 'github']}
              >
                <HelpCircle size={14} strokeWidth={1.75} aria-hidden="true" />
                <span>Documentação</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
